import { Alert, Form, Modal, Select, Tag } from "antd";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { components } from "@qorakul/shared/api";
import { api } from "../app/api.ts";
import { allRoles, roleTagColor, type Role } from "../app/roles.ts";
import { applyProblemToForm, requestFailure, type RequestFailure } from "../app/form-errors.ts";

type UserDetails = components["schemas"]["UserDetails"];

interface Props {
  user: UserDetails | null;
  onClose(): void;
  onSaved(user: UserDetails): void;
}

export function EditRolesModal({ user, onClose, onSaved }: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<{ roles: Role[] }>();
  const [serverErrors, setServerErrors] = useState<string[]>([]);

  useEffect(() => {
    if (user) {
      form.setFieldsValue({ roles: user.roles });
      setServerErrors([]);
    }
  }, [user, form]);

  const mutation = useMutation({
    mutationFn: async (roles: Role[]) => {
      if (!user) throw new Error("no user");
      const { data, error, response } = await api.PUT("/users/{id}/roles", { params: { path: { id: user.id } }, body: { roles } });
      if (!data) throw requestFailure(error, response.status);
      return data;
    },
    onSuccess: async (saved) => {
      await queryClient.invalidateQueries({ queryKey: ["users"] });
      onSaved(saved);
    },
    onError: (failure: RequestFailure) => setServerErrors(applyProblemToForm(failure, form, ["roles"], t("app.error"))),
  });

  return (
    <Modal
      title={t("user.editRolesTitle", { name: user?.displayName ?? "" })}
      open={user !== null}
      onCancel={onClose}
      onOk={() => form.submit()}
      okText={t("user.save")}
      cancelText={t("user.cancel")}
      confirmLoading={mutation.isPending}
      destroyOnHidden
    >
      {serverErrors.length > 0 && (
        <Alert type="error" showIcon style={{ marginBottom: 16 }} role="alert" title={serverErrors.map((m) => <div key={m}>{m}</div>)} />
      )}
      <Form form={form} layout="vertical" requiredMark={false} onFinish={(values) => mutation.mutate(values.roles)}>
        <Form.Item name="roles" label={t("user.roles")} extra={t("user.rolesHint")} rules={[{ required: true, type: "array", min: 1, message: t("user.validation.roles") }]}>
          <Select<Role[]>
            mode="multiple"
            autoFocus
            showSearch={{ optionFilterProp: "label" }}
            placeholder={t("user.rolesPlaceholder")}
            options={allRoles.map((r) => ({ value: r, label: t(`roles.${r}`) }))}
            tagRender={({ label, value, closable, onClose: close }) => (
              <Tag color={roleTagColor[value as Role]} closable={closable} onClose={close} style={{ marginInlineEnd: 4 }}>
                {label}
              </Tag>
            )}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}
